import { logManager } from '../logs'

// Libraries.
import EventEmitter from 'eventemitter3'

/**
 * Wrapper object for a native MediaStreamTrack object.
 * Ref: https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack
 * @method Track
 */
export default function Track (mediaTrack, mediaStream) {
  const log = logManager.getLogger('Track', mediaTrack.id)
  log.info(`Creating new ${mediaTrack.kind} Track.`)

  // Internal variables.
  const id = mediaTrack.id
  const track = mediaTrack
  let stream = mediaStream
  let isLocalTrack
  let constraints = {}
  const emitter = new EventEmitter()

  /**
   * When a track ends, the Track itself doesn't do anything about it.
   * It "bubbles" the event up to be handled at a higher level.
   */
  track.onended = event => {
    // Check whether this function was called because a native event was emitted
    //    or if it was called manually by the SDK.
    const isNative = event instanceof Event

    const endpoint = isLocalTrack ? 'local' : 'remote'
    const trigger = isNative ? 'unsolicited' : 'solicited'
    log.debug(`Track ended (${endpoint}, ${trigger}).`, event)

    emitter.emit('ended', {
      trackId: track.id,
      /*
       * If an event triggered this listener, then it was a remote notification
       *    (for remote track) or browser action (for local track) that caused it.
       *    If it was a local track, then the track ending was not part of a
       *    renegotiation and something will need to handle this situation.
       *
       * Otherwise, the SDK called `track.onended()` when we stopped the track as
       *    part of an operation. The operation should handle the track being
       *    ended since it was solicited.
       */
      isUnsolicited: isNative
    })
  }

  /**
   * When a track is muted, we forward this to any listener.
   * This handler is typically invoked by actions triggered outside the
   * direct control of the webrtc SDK.
   */
  track.onmute = event => {
    log.debug('Event emitted: ', event)
    emitter.emit('muted', {
      trackId: track.id,
      mediaId: stream.id,
      isLocal: isLocalTrack
    })
  }

  /**
   * When a track is unmuted, we forward this to any listener.
   * This handler is typically invoked by actions triggered outside the
   * direct control of the webrtc SDK.
   */
  track.onunmute = event => {
    log.debug('Event emitted: ', event)
    emitter.emit('unmuted', {
      trackId: track.id,
      mediaId: stream.id,
      isLocal: isLocalTrack
    })
  }

  function setIsLocal (isLocal) {
    isLocalTrack = isLocal
  }

  function isLocal () {
    return isLocalTrack
  }

  function setStream (newStream) {
    stream = newStream
  }
  function getStream () {
    return stream
  }

  /**
   * Retrieve a snapshot of the Track object's current state.
   * @method getState
   * @return {Object}
   */
  function getState () {
    return {
      id,
      streamId: stream.id,
      kind: track.kind,
      isLocal: isLocalTrack,
      label: track.label,
      muted: track.muted,
      enabled: track.enabled,
      state: track.readyState
    }
  }

  /**
   * Set this Track to be disabled and disallow the Track to render the source stream.
   * @method mute
   */
  function mute () {
    track.enabled = false
  }

  /**
   * Set this Track to be enabled and allow the Track to render the source stream.
   * @method unmute
   */
  function unmute () {
    track.enabled = true
  }

  /**
   * Calls native stop() function to deassociate the source and the track.
   * @method stop
   */
  function stop () {
    if (track.readyState === 'ended') {
      log.debug('Track already ended.')
      return
    }

    track.stop()
    /**
     * Treat stopping the track the same as it being ended.
     * Normally, onended is not triggered when `stop` is called, only when it is
     *    "remotely ended".
     */
    track.onended()
  }

  /**
   * Gets the currently set constraints for the track.
   * @method getConstraints
   * @return {Object}
   */
  function getConstraints () {
    return constraints
  }

  /**
   * Set the values for constrainable properties of the track.
   * @method applyConstraints
   * @param  {Object} constraints The list of constrainable properties.
   */
  function setConstraints (constr) {
    constraints = constr
  }

  function on (...args) {
    return emitter.on(...args)
  }

  function once (...args) {
    return emitter.once(...args)
  }

  function off (...args) {
    return emitter.off(...args)
  }

  /**
   * The exposed API.
   */
  return {
    id,
    // Track APIs.
    getState,
    mute,
    unmute,
    stop,
    getConstraints,
    setConstraints,
    // Event APIs.
    on,
    off,
    once,
    // The native track and stream was accessible before, so it was
    //    used when it probably shouldn't have been.
    // TODO: Find a better solution.
    track,
    setStream,
    getStream,
    setIsLocal,
    isLocal
  }
}
